<template>
  <div class="d-flex flex-column w-100 choice-grid">
    <label
      v-if="itemModel.vars.label"
      :class="
        'form-label' +
        (itemModel.vars.label_attr && itemModel.vars.label_attr.class ? ' ' + itemModel.vars.label_attr.class : '') +
        (itemModel.vars.required ? ' required' : '')
      "
    >
      {{ itemModel.vars.label }}{{ itemModel.vars.required ? "*" : "" }}
      <span
        v-if="itemModel.vars.help && !isMobile()"
        v-tooltip="{ title: itemModel.vars.help, textWrap: true }"
        class="form-info ms-1"
      >
        <FontAwesomeIcon icon="fa-light fa-circle-question" />
      </span>
    </label>
    <div v-if="isMobile() && itemModel.vars.help" class="form-info">
      <FontAwesomeIcon icon="fa-light fa-circle-question" />
      {{ itemModel.vars.help }}
    </div>

    <div class="choice-grid__search">
      <FontAwesomeIcon icon="fa-light fa-magnifying-glass" />
      <InputTypeComponent :model="searchModel" />
    </div>

    <div v-if="selectedChips.length" class="choice-grid__selected">
      <BadgeComponent
        v-for="chip in selectedChips"
        :key="chip.id"
        :text="chip.label"
        :dot="false"
        :className="chip.hasColor ? chip.color : 'primary'"
        compact
      >
        <button
          type="button"
          class="choice-grid__chip-remove ms-2"
          :disabled="isDisabled"
          :aria-label="$t('choiceGrid.remove', { name: chip.label })"
          @click="remove(chip.id)"
        >
          <FontAwesomeIcon icon="fa-light fa-xmark" aria-hidden="true" />
        </button>
      </BadgeComponent>
    </div>

    <div class="choice-grid__grid-wrapper">
      <div v-if="!choices.length" class="choice-grid__empty">
        {{ $t("global.empty.no_data_available") }}
      </div>
      <div v-else :id="gridId" class="choice-grid__grid">
        <div v-if="showEmptySearch" class="choice-grid__empty">
          {{ $t("global.empty.no_data_available") }}
        </div>
        <template v-else>
          <button
            v-for="choice in visibleChoices"
            :key="choice.value"
            type="button"
            class="choice-grid__card"
            :class="{ '-selected': isSelected(choice) }"
            :disabled="isDisabled"
            :aria-pressed="isSelected(choice)"
            @click="toggle(choice)"
          >
            <div
              class="choice-grid__icon"
              :class="{ '-default': !hasColor(choice) }"
              :style="hasColor(choice) ? { background: cardBg(choice), color: choiceColor(choice) } : {}"
            >
              <FontAwesomeIcon :icon="'fa-light fa-' + choiceIcon(choice)" />
            </div>
            <div class="choice-grid__body">
              <div class="choice-grid__label">
                {{ choice.label }}
              </div>
              <div v-if="choiceCountLabel(choice)" class="choice-grid__count">
                {{ choiceCountLabel(choice) }}
              </div>
            </div>
          </button>
        </template>
      </div>
    </div>

    <button
      v-if="!isSearching && choices.length"
      type="button"
      class="choice-grid__toggle"
      :class="{ '-expanded': expanded }"
      :disabled="isDisabled"
      :aria-expanded="expanded"
      :aria-controls="gridId"
      @click="expanded = !expanded"
    >
      <span>{{ toggleLabel }}</span>
      <FontAwesomeIcon icon="fa-light fa-chevron-down" />
    </button>

    <div v-if="itemModel.vars.error" class="invalid-feedback d-block">
      {{ itemModel.vars.error }}
    </div>

    <BlockLoader :loading="!!itemModel.loading" />
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import InputTypeComponent from "@/components/form/InputTypeComponent.vue";
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import BadgeComponent from "@/components/utils/badge/BadgeComponent.vue";
import { colorManager } from "@/services/platform/color-manager";
import { formValidator } from "@/services/form/form-validator";
import { formManager } from "@/services/form/form-manager";
import { choiceIdentityManager } from "@/services/form/choice-identity-manager";

// Alpha du fond teinté dérivé de la couleur d'accent d'un choix (icône + chip sélectionné) -
// même valeur que BadgeComponent.vue pour rester cohérent visuellement dans toute l'app.
const TINT_ALPHA = 0.15;
// Contrat avec le back (TrainingDomain::DEFAULT_COLOR, cf. hasColor() plus bas) : contrairement à
// ICON_FALLBACK_COLOR/DOMAIN_COLOR_FALLBACK (color-manager.js), cette valeur ne doit PAS être
// remplacée par un token SCSS - une couleur de domaine identique à celle-ci est lue comme "pas de
// couleur propre", quelle que soit l'esthétique front.
const DEFAULT_COLOR = "#999999";
const DEFAULT_ICON = "graduation-cap";

// Composant générique autonome (choice.attr['data-color']/['data-icon']/['data-count-label'] par
// choix, attr['data-choice-grid'] au niveau du champ - cf. formManager.isChoiceGridType()) :
// grille de cartes colorées avec recherche et chips retirables, pour un choix multiple à
// nombreuses options. N'appartient à aucun composant existant (pas de CheckboxTypeComponent/
// CheckboxInputComponent touché) - contrat identique à ColorPickerComponent/IconPickerComponent
// (model unique, emits:['update'], pas de fetch propre).
export default {
  name: "ChoiceGridTypeComponent",
  components: { FontAwesomeIcon, BadgeComponent, InputTypeComponent, BlockLoader },
  props: {
    model: {
      type: Object,
      required: true,
    },
  },
  emits: ["update", "ajaxTargetLoad", "ajaxTargetTrig", "resetInputs"],
  data() {
    return {
      itemModel: this.model,
      // Modèle de formulaire du champ de recherche (rendu par InputTypeComponent) ; le libellé, masqué visuellement,
      // reprend le placeholder (RGAA 11.1/11.2).
      searchModel: {
        vars: {
          name: `${this.model.vars.name}Search`,
          id: `${this.model.vars.name}-search`,
          value: "",
          required: false,
          disabled: false,
          block_prefixes: ["filter", "text"],
          label: "",
          label_attr: { class: "visually-hidden" },
          attr: { placeholder: "" },
        },
      },
      expanded: false,
    };
  },
  computed: {
    search: {
      get() {
        return this.searchModel.vars.value ?? "";
      },
      set(value) {
        this.searchModel.vars.value = value;
      },
    },
    // Aligné sur CheckboxTypeComponent/SelectTypeComponent (revue Loïc, ST304-TA-4918) : readonly
    // et disabled désactivent tous deux les interactions, readonly n'ayant pas de rendu visuel
    // distinct ici (pas de valeur "affichée en lecture" pour une grille de cartes).
    isDisabled() {
      return !!(this.itemModel.vars.disabled || (this.itemModel.vars.attr && this.itemModel.vars.attr.readonly));
    },
    // Object.values() (pas un accès direct) : le cascade data-ajax-load (formManager.fieldLoad(),
    // FormComponent::ajaxTargetLoad()) remplace vars.choices par un objet à clés numériques
    // ({...data}), pas un tableau - même défense que SelectTypeComponent/CheckboxTypeComponent.
    choices() {
      return Object.values(this.itemModel.vars.choices || []);
    },
    selectedValues() {
      return this.itemModel.vars.value || [];
    },
    isSearching() {
      return this.search.trim().length > 0;
    },
    // Revue Loïc (RGAA 8.2) : id/aria-controls dérivés du nom du champ - un id en dur dupliquerait
    // dès que deux ChoiceGridTypeComponent coexistent sur la même page.
    gridId() {
      return `${this.itemModel.vars.name}-grid`;
    },
    // Rien affiché tant que la recherche n'est pas utilisée ou que le bouton "Afficher tout" n'a
    // pas été cliqué.
    // Revue Loïc : filter() local, pas un appel ajax - un domaine du catalogue paginé non encore
    // chargé dans vars.choices reste introuvable par la recherche. Limite assumée pour l'instant
    // (pas de data-ajax-url ni d'événement de recherche serveur ici) ; un renvoi au back suppose
    // une décision produit sur le volume réel du catalogue, hors périmètre de ce composant front.
    visibleChoices() {
      const term = this.search.trim().toLowerCase();
      if (term) {
        return this.choices.filter((choice) => choice.label.toLowerCase().includes(term));
      }
      return this.expanded ? this.choices : [];
    },
    showEmptySearch() {
      return this.isSearching && !this.visibleChoices.length;
    },
    toggleLabel() {
      return this.expanded ? this.$t("choiceGrid.collapse") : this.$t("choiceGrid.showAll");
    },
    searchPlaceholder() {
      return this.$t("choiceGrid.search");
    },
    selectedChips() {
      return this.selectedValues.map((value) => {
        const identity = choiceIdentityManager.identity(value);
        const choice = this.choices.find((item) => item.value === identity);
        return {
          id: identity,
          label: choice ? choice.label : value?.name,
          color: choice ? this.choiceColor(choice) : DEFAULT_COLOR,
          hasColor: choice ? this.hasColor(choice) : false,
        };
      });
    },
  },
  watch: {
    searchPlaceholder: {
      immediate: true,
      handler(placeholder) {
        this.searchModel.vars.attr.placeholder = placeholder;
        this.searchModel.vars.label = placeholder;
      },
    },
    isDisabled: {
      immediate: true,
      handler(disabled) {
        this.searchModel.vars.disabled = disabled;
      },
    },
    // Un nouveau lot de choices (ex. cascade data-ajax-load après changement du champ dont ce champ
    // dépend) ne garde de la sélection précédente que les valeurs encore présentes dedans - une
    // présélection (reprise) ou un filtre posé par l'appelant ne doit pas être perdu en bloc.
    "itemModel.vars.choices"() {
      this.search = "";
      this.expanded = false;
      if (this.itemModel.vars.value && this.itemModel.vars.value.length) {
        const validIdentities = new Set(this.choices.map((choice) => choice.value));
        const filtered = this.itemModel.vars.value.filter((value) =>
          validIdentities.has(choiceIdentityManager.identity(value)),
        );
        if (filtered.length !== this.itemModel.vars.value.length) {
          this.itemModel.vars.value = filtered;
          this.$emit("update", this.itemModel.vars.value);
        }
      }
    },
  },
  created() {
    if (!this.itemModel.vars.value) {
      this.itemModel.vars.value = [];
    }
  },
  methods: {
    isMobile() {
      return window.innerWidth < 992;
    },
    // Le back sert DEFAULT_COLOR pour un domaine sans couleur propre (TrainingDomain::DEFAULT_COLOR) :
    // c'est, comme l'absence de data-color, « pas de couleur définie » - l'icône prend alors le style
    // par défaut (.-default) plutôt qu'un gris.
    hasColor(choice) {
      const color = choice.attr && choice.attr["data-color"];
      return !!color && color.toLowerCase() !== DEFAULT_COLOR;
    },
    choiceColor(choice) {
      return (choice.attr && choice.attr["data-color"]) || DEFAULT_COLOR;
    },
    choiceIcon(choice) {
      return (choice.attr && choice.attr["data-icon"]) || DEFAULT_ICON;
    },
    choiceCountLabel(choice) {
      return (choice.attr && choice.attr["data-count-label"]) || null;
    },
    cardBg(choice) {
      return colorManager.hexWithAlpha(this.choiceColor(choice), TINT_ALPHA);
    },
    isSelected(choice) {
      return this.selectedValues.some((value) => choiceIdentityManager.identity(value) === choice.value);
    },
    toggle(choice) {
      if (this.isSelected(choice)) {
        this.itemModel.vars.value = this.selectedValues.filter(
          (value) => choiceIdentityManager.identity(value) !== choice.value,
        );
      } else {
        this.itemModel.vars.value = [...this.selectedValues, choice.data];
      }
      this.apply();
    },
    remove(identity) {
      this.itemModel.vars.value = this.selectedValues.filter(
        (value) => choiceIdentityManager.identity(value) !== identity,
      );
      this.apply();
    },
    apply() {
      formValidator.validModel(this.itemModel);
      this.$emit("update", this.itemModel.vars.value);
      this.reloadLinkedTargets();
    },
    // Cascade générique (data-ajax-load + data-ajax-linked-to), même mécanisme que ChoiceChipTypeComponent :
    // FormComponent.ajaxTargetTrig() fait l'appel préfixé des champs liés ; une sélection vide vide la cible.
    reloadLinkedTargets() {
      const attr = this.itemModel.vars.attr;
      const ajaxTargets = attr?.["data-ajax-load"];
      if (!ajaxTargets) {
        return;
      }
      if (attr["data-ajax-search"] || formManager.getAjaxIdentifier(this.itemModel)) {
        this.$emit("ajaxTargetTrig", { trigger: this.itemModel });
      } else {
        Object.keys(ajaxTargets).forEach((target) => this.$emit("ajaxTargetLoad", { choices: {}, target }));
      }
      this.$emit("resetInputs", { item: ajaxTargets });
    },
  },
};
</script>

<style lang="scss" scoped>
// Revue Loïc (duplication) : partial partagé avec ChoiceCardListTypeComponent (__search,
// __toggle/__loadMore, __empty), même pattern que src/components/skills/_skill-shared.scss.
@import "./choice-list-shared.scss";

.choice-grid {
  position: relative;

  &__search {
    @extend %choice-search;
  }

  &__selected {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    margin-bottom: 9px;
  }

  &__chip-remove {
    display: inline-flex;
    align-items: center;
    padding: 0;
    border: none;
    background: none;
    color: inherit;
    font-size: inherit;
    line-height: 1;
    cursor: pointer;
    opacity: 0.8;

    &:hover:not(:disabled) {
      opacity: 1;
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  &__grid-wrapper {
    position: relative;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  &__card {
    width: 100%;
    // ST304-TA-4574 : les cartes d'une même rangée gardent la même hauteur même quand un
    // nom de domaine très long (ou un compteur sur 2 lignes) grandit son contenu — sans ça,
    // la grille se désaligne (cf. landing « ÁMBITOS DE FORMACIÓN »).
    height: 100%;
    // Revue Loïc (RGAA 3.3) : $m-color_4 sur blanc ne tient que 1,68:1, état non sélectionné
    // (sélectionné : $success, cf. .-selected) - $m-color_6 tient 3,24:1.
    border: 1.5px solid $m-color_6;
    border-radius: 11px;
    padding: 9px;
    display: flex;
    align-items: center;
    gap: 9px;
    cursor: pointer;
    transition: 0.15s;
    background: $white;
    font-family: inherit;
    text-align: left;

    @include dark-theme {
      background: $dark-theme-bg;
      border-color: $m-color_12;
    }

    &:hover {
      border-color: $dark-grey;
    }

    &.-selected {
      border-color: $success;
      background: rgba($success, 0.1);

      @include dark-theme {
        background: rgba($success, 0.1);
        border-color: $success;
      }
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__icon {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &.-default {
      background: rgba($primary, 0.1);
      color: $primary;
    }
  }

  &__label {
    font-size: 10.5px;
    font-weight: 700;
    color: $primary;
    line-height: 1.25;
    // ST304-TA-4574 : un nom très long (ex. « PlacementDomainCatalogNominalDomain », sans
    // espaces donc insécable) est clampé à 2 lignes avec ellipsis au lieu d'étirer/déborder
    // sa carte et de désaligner la rangée.
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;

    @include dark-theme {
      color: $white;
    }
  }

  &__count {
    font-size: 10px;
    color: $primary;
    font-weight: 400;
    // ST304-TA-4574 : le compteur ne grandit jamais la carte (ellipsis sur 1 ligne).
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @include dark-theme {
      color: $light-grey;
    }
  }

  &__empty {
    @extend %choice-empty;
    grid-column: 1 / -1;
    padding: 16px;
    font-size: 11.5px;
  }

  &__toggle {
    @extend %choice-expand-btn;

    svg {
      transition: transform 0.2s;
    }

    &.-expanded svg {
      transform: rotate(180deg);
    }
  }

  // En dessous de 380px (téléphones très étroits, ex. iPhone SE 1ère/2ème génération en
  // 320-375px), 2 colonnes rendent &__label illisible (clamp à 2 lignes, police 10-10,5px déjà
  // au plus bas) - repli à 1 colonne plutôt que de réduire encore la police.
  @media (max-width: 380px) {
    &__grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
